import axiosInstance from '@/setup/axios/axiosInstance';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import {
  InventoryStoreListResponse,
  InventoryStorePayload,
  InventoryStoreQueryParams,
  InventoryStoreRow,
  InventoryStoreAddressValue,
  InventoryStoreStatusValue,
} from '../models/StoreModel';

interface StoreApiItem {
  id: string;
  code: string;
  name: string;
  status: InventoryStoreStatusValue;
  status_display: string;
  status_code?: string;
  company_id: string;
  company_name: string;
  address?: InventoryStoreAddressValue;
  address_obj?: InventoryStoreAddressValue;
  created_at?: string;
  updated_at?: string;
}

interface StoreListApiResponse {
  results?: StoreApiItem[];
  count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  has_next?: boolean;
  has_previous?: boolean;
}

const resolveStoreStatus = (status: InventoryStoreStatusValue | undefined) => {
  if (status && typeof status === 'object') {
    return {
      id: String(status.id || status.code || ''),
      label: String(status.name || status.code || ''),
      color: String(status.color || ''),
    };
  }

  return {
    id: String(status || ''),
    label: String(status || ''),
    color: '',
  };
};

const mapStore = (store: StoreApiItem): InventoryStoreRow => {
  const status = resolveStoreStatus(store.status);

  return {
    id: store.id,
    code: store.code,
    name: store.name,
    status: status.id,
    status_label: store.status_display || status.label,
    status_color: status.color,
    company_id: store.company_id,
    company_name: store.company_name,
    address: store.address_obj || store.address || '',
    created_at: store.created_at,
    updated_at: store.updated_at,
  };
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveStoresCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'global' || resolved === 'all' || !UUID_PATTERN.test(resolved)) {
    return undefined;
  }
  return resolved;
};

export async function getStores(params: InventoryStoreQueryParams = {}): Promise<InventoryStoreListResponse> {
  const searchParams = new URLSearchParams();

  if (params.search) {
    searchParams.append('search', params.search);
  }
  if (params.status && params.status !== 'all') {
    searchParams.append('status', params.status);
  }
  const companyId = resolveStoresCompanyId(params.company_id);
  if (companyId) {
    searchParams.append('company', companyId);
  }
  if (params.page) {
    searchParams.append('page', String(params.page));
  }
  if (params.page_size) {
    searchParams.append('page_size', String(params.page_size));
  }

  const response = await axiosInstance.get<StoreListApiResponse>(`/inventory/stores/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`);
  const payload = response.data;

  return {
    results: (payload.results || []).map(mapStore),
    count: payload.count || 0,
    page: payload.page || 1,
    page_size: payload.page_size || params.page_size || DEFAULT_PAGE_SIZE,
    total_pages: payload.total_pages || 1,
    has_next: payload.has_next || false,
    has_previous: payload.has_previous || false,
  };
}

export async function getStore(id: string): Promise<InventoryStoreRow> {
  const response = await axiosInstance.get<StoreApiItem>(`/inventory/stores/${id}/`);
  return mapStore(response.data);
}

export async function createStore(payload: InventoryStorePayload): Promise<InventoryStoreRow> {
  const response = await axiosInstance.post<StoreApiItem>('/inventory/stores/', payload);
  return mapStore(response.data);
}

export async function updateStore(id: string, payload: InventoryStorePayload): Promise<InventoryStoreRow> {
  const response = await axiosInstance.put<StoreApiItem>(`/inventory/stores/${id}/`, payload);
  return mapStore(response.data);
}

export async function deleteStore(id: string): Promise<void> {
  await axiosInstance.delete(`/inventory/stores/${id}/`);
}
